import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link as Linked,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Slide,
  styled,
} from "@mui/material";

import {TransitionProps} from "@mui/material/transitions";

import React from "react";
import {useForm} from "react-hook-form";
import * as archiveFileApi from "../../api/archiveApi/archiveFileApi";
import * as archiveFolderApi from "../../api/archiveApi/archiveFolderApi";
import MWCaption from "../../components/MWCaption";
import SubTitle from "../../components/SubTitle";
import {useCreateMutation, useGetItemQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseItem, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import * as Params from "../../models/clientModels/interfaces/apiParams";
import {KPFolder} from "../../models/serverModels/archive/KPFolder";

import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import HomeIcon from "@mui/icons-material/Home";
import {useQueryClient} from "react-query";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {levelSelector, tokenSelector} from "../../Recoil/Atom/authState";
import {snackBarState, snackBarToggleState} from "../../Recoil/Atom/snackBarState";
import {ReactComponent as FolderIcon} from "../../assets/images/folder.svg";
import MWProgress from "../../components/MWProgress";
import {KPFile, TypeSelectIcon} from "../../models/serverModels/archive/KPFile";
import {SubMenuGroup} from "../../styledComponents/StyledListContainer";

const CategoryFormContainer = styled(Grid)(({theme}) => ({
  display: "flex",
  justifyContent: "center",
  paddingTop: "48px",
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  "& p": {
    marginTop: "8px",
    textAlign: "center",
  },
}));

const CategoryFormContent = styled(Grid)(({theme}) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "240px",
  backgroundColor: theme.palette.custom.main,

  border: `2px dotted ${theme.palette.primary.main}`,
  borderRadius: "16px",
  boxShadow:
    "rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem",

  "& label": {
    fontWeight: 600,
    paddingRight: "8px",
  },
  "& input": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 0,
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ArchiveList = () => {
  const {id} = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const userLevel = useRecoilValue(levelSelector);

  const tokenState = useRecoilValue(tokenSelector);
  const setAlertState = useSetRecoilState(alertStateAtom);

  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const [open, setOpen] = React.useState(false);
  const [qrCodeID, setQRCodeID] = React.useState(0); // Smoking Scully 추가

  const {
    data: archiveList,
    isLoading,
    isFetching,
  } = useGetItemQuery<ResponseItem<KPFolder>>({
    key: ["ArchiveList", id ?? 0],
    api: () => archiveFolderApi.getItem({id: parseInt(id!) ?? 0, token: tokenState}),
  });

  const {mutate: createMutate} = useCreateMutation<ResponseItem<KPFile>, any>({
    api: archiveFileApi.create,
    options: {
      onSuccess: () => {
        setSnackBarToggle(true);
        setSnackBarState("create");
        navigate(`/Archive/${id || 0}`);
        queryClient.invalidateQueries("ArchiveList");
      },
      onError: () => {
        setSnackBarToggle(true);
        setSnackBarState("failed");
      },
    },
  });

  const {mutate: folderRemoveMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: archiveFolderApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("ArchiveList");
      },
    },
  });
  const {mutate: fileRemoveMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: archiveFileApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("ArchiveList");
      },
    },
  });

  const {register} = useForm();

  // note formData function
  const formDataHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const data = event.target.files as FileList;
    const formData = new FormData();

    let totalBytes = 0;

    for (let i = 0; i < data.length; i++) {
      formData.append(`files`, data[i]);
      totalBytes += data[i].size;
    }

    // 100MB 초과시
    if (totalBytes > 104857600) {
      // 메시지 표시
      // 임시 조치
      alert("100MB를 초과하였습니다");
      return;
    }

    formData.append("folderUID", id ?? "0");
    formData.append("token", tokenState);
    createMutate(formData);
    event.target.value = "";
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  function openQRCdoe(itemUID: number) {
    setQRCodeID(itemUID);
    setOpen(true);
  }

  return isLoading && isFetching ? (
    <MWProgress />
  ) : (
    <React.Fragment>
      <SubTitle subTitle="공정안전보고서">
        <Button variant="contained" color="primary" size="small" sx={{marginLeft: "18px"}}>
          <a href={`/API/DownloadArchives/${tokenState}`} target="_blank" style={{color: "white"}}>
            ZIP파일로 저장
          </a>
        </Button>
      </SubTitle>
      <SubMenuGroup>
        <div role="presentation">
          {/* <Breadcrumbs aria-label="breadcrumb" sx={{paddingLeft: "8px"}}>
            <Linked
              underline="hover"
              sx={{display: "flex", alignItems: "center", cursor: "pointer"}}
              color="inherit"
              onClick={() => {
                navigate("/Archive");
              }}
            >
              <HomeIcon sx={{mr: 0.5}} fontSize="inherit" />
              Home
            </Linked>
            {archiveList?.item?.previousFolders.map((item, index) => (
              <Linked
                key={item.uid}
                underline="hover"
                sx={{display: "flex", alignItems: "center", cursor: "pointer"}}
                color="inherit"
                onClick={() => navigate(-1)}
              >
                {item.name}
              </Linked>
            ))}
            {archiveList?.item?.name && (
              <Linked
                sx={{
                  display: "flex",
                  fontWeight: 500,
                  alignItems: "center",
                  textDecoration: "none",
                }}
                color="primary"
              >
                {archiveList?.item?.name}
              </Linked>
            )}2
          </Breadcrumbs> */}
        </div>
        {userLevel === 3 && (
          <div>
            <Button
              color="primary"
              variant="contained"
              size="small"
              sx={{marginRight: "8px"}}
              onClick={() => {
                navigate(`/Archive/Create/${archiveList?.item?.uid}`);
              }}
            >
              폴더 추가
            </Button>
            <Button color="primary" variant="outlined" size="small">
              <label htmlFor="file">파일추가</label>
            </Button>
          </div>
        )}
      </SubMenuGroup>
      <MWCaption
        title="공정안전 보고서"
        icon={<CreateNewFolderIcon />}
        color="#4E342E"
        // link={`/Archive/Create/${archiveList?.item?.uid}`}
      />
      <CategoryFormContainer container>
        {userLevel === 3 && (
          <CategoryFormContent item xs={12} sm={6} md={8} xl={8}>
            <input
              type="file"
              id="file"
              multiple
              {...register("file")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => formDataHandler(event)}
            />

            <CloudDownloadIcon color="success" style={{fontSize: "68px"}} />
            <p>Drag and Drop</p>
          </CategoryFormContent>
        )}

        <Grid item xs={12} sm={6} md={8} xl={8}>
          <List>
            {archiveList?.item?.folders.map((item) => (
              <ListItem key={item.uid} disablePadding>
                <ListItemButton>
                  <ListItemIcon>
                    <FolderIcon />
                  </ListItemIcon>
                  <List
                    onClick={() => {
                      navigate(`/Archive/${item.uid}`);
                    }}
                  >
                    <ListItemText primary={item.name} />
                  </List>
                </ListItemButton>

                {userLevel === 3 && (
                  <Box>
                    <IconButton
                      aria-label="edit"
                      color="primary"
                      onClick={() => {
                        navigate(`/Archive/Edit/${item.uid}`, {state: item.folderUID});
                      }}
                    >
                      <DriveFileRenameOutlineIcon color="primary" />
                    </IconButton>

                    <IconButton
                      aria-label="delete"
                      color="secondary"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 폴더를 삭제하시겠습니까?",
                          execution: () => folderRemoveMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      <HighlightOffIcon color="secondary" sx={{marginLeft: "8px"}} />
                    </IconButton>
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid item xs={12} sm={6} md={8} xl={8}>
          <List>
            {archiveList?.item?.files.map((item, index) => (
              <ListItem key={item.uid} disablePadding>
                <ListItemButton>
                  <ListItemIcon>{TypeSelectIcon(item.type)}</ListItemIcon>
                  <Linked
                    href={`/API/file/${item.uid}?token=${tokenState}`}
                    underline="hover"
                    color="inherit"
                  >
                    <ListItemText
                      sx={{marginRight: "30px"}}
                      primary={`${item.name}${item.extension}`}
                    />
                  </Linked>
                </ListItemButton>
                <Box sx={{display: "flex", alignItems: "center", paddingLeft: "16px"}}>
                  {item.entryDate.toString()?.substring(0, 10)}
                  <Button onClick={() => openQRCdoe(item.uid)} style={{whiteSpace: "nowrap"}}>
                    QR Code
                  </Button>
                  {userLevel === 3 && (
                    <IconButton
                      aria-label="delete"
                      color="secondary"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 파일를 삭제하시겠습니까?",
                          execution: () => fileRemoveMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      <HighlightOffIcon color="secondary" sx={{marginLeft: "16px"}} />
                    </IconButton>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Grid>
      </CategoryFormContainer>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle align="center" fontWeight={600} mb="12px">
          QR CODE
        </DialogTitle>
        <DialogContent sx={{display: "flex", justifyContent: "center"}}>
          <img
            src={`/API/GenerateFileQRCode?token=${tokenState}&id=${qrCodeID}`}
            alt="QRCode"
            width="80%"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>닫기</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ArchiveList;
